import React, { useState, useEffect, useMemo, useRef } from "react";
import { trackEvent } from "../../utils/analytics";
import countriesJson from "../../utils/countries.json";
// import React from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  Autocomplete,
  TextField,
  Badge,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { debounce, fetchLocationSearch } from "../../utils/boatApi";
import { SearchOption } from "../../utils/mockDataAutoSearch";
import { RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  setAllData,
  setLocalSearchData,
  setSelectedLocation as setLocation,
} from "../../store/searchSlice";
import "../../Header.css";
import SailRangePicker from "./SailRangePicker";
import { useLocation } from "react-router-dom";

interface HeaderWithSearchProps {
  onSearch: (
    checkIn: string,
    checkOut: string,
    country: string,
    boatType: string,
    cabins: number,
    city: string,
    id: number,
    type: string,
    page: number
  ) => void;
}

const HeaderWithSearch: React.FC<HeaderWithSearchProps> = ({ onSearch }) => {
  const searchData = useSelector((state: RootState) => state.search);
  const [searchResults, setSearchResults] = useState<SearchOption[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBoatType, setSelectedBoatType] = useState<any>("");
  const [checkIn, setCheckIn] = useState<string>("");
  const [checkOut, setCheckOut] = useState<string>("");
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [loadingClickSearch, setLoadingClickSearch] = useState(false);
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedLocation, setSelectedLocation] = useState<any | null>("");
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const containerStyles = {
    border: "1px solid #d3d3d3",
    borderRadius: "8px",
    width: "100%",
  };

  const labelStyles = {
    fontSize: "14px",
    fontWeight: "700",
    color: "#484a4b",
    pt: "0.5rem",
    lineHeight: "0.5rem",
  };

  useEffect(() => {
    if (searchData.searchResults?.count > 0) {
      // setSelectedLocation(searchData?.selectedLocation);
      // setBeds(searchData?.beds);
      // setCheckIn(searchData?.checkIn);
      // setCheckOut(searchData?.checkOut);
      // setSelectedBoatType(searchData?.selectedBoatType);
      // setBoatTypes(searchData?.boatTypes);
    }
  }, [searchData]);

  const locationRequestId = useRef(0);
  const handleLocationSearch = useRef(
    debounce(async (query: string) => {
      if (!query || query.length < 2) return;
      const reqId = ++locationRequestId.current;
      setLoadingLocation(true);
      try {
        const results = await fetchLocationSearch(query);
        if (reqId !== locationRequestId.current) return; // stale response
        setSearchResults(results);
        setError(null);
      } catch {
        if (reqId !== locationRequestId.current) return;
        setError("Failed to fetch locations");
      } finally {
        if (reqId === locationRequestId.current) setLoadingLocation(false);
      }
    }, 500)
  ).current;
  useEffect(() => { setExpandedIds(new Set()); }, [searchResults]);

  const { parentMap, hasChildren } = useMemo(() => {
    const parentMap = new Map<number, string>();
    const hasChildren = new Set<string>();
    let groupKey: string | null = null;
    searchResults.forEach((opt, idx) => {
      const t = (opt as any).type;
      if (t === 'sailing_area' || t === 'island' || t === 'city') {
        groupKey = `${t}-${(opt as any).id ?? opt.city ?? opt.name}`;
      } else if (groupKey && opt.isSubItem) {
        parentMap.set(idx, groupKey);
        hasChildren.add(groupKey);
      }
    });
    return { parentMap, hasChildren };
  }, [searchResults]);

  const displayedOptions = useMemo(() =>
    searchResults.filter((opt, idx) => {
      const t = (opt as any).type;
      if (t === 'sailing_area' || t === 'island' || t === 'city') return true;
      const pk = parentMap.get(idx);
      if (!pk) return true;
      return expandedIds.has(pk);
    }),
  [searchResults, parentMap, expandedIds]);

  const optKey = (opt: SearchOption) => `${(opt as any).type}-${(opt as any).id ?? opt.city ?? opt.name}`;

  interface BoatTypeOption {
    label: string;
    id: number;
  }
  interface LocalSearchData {
    city?: string;
    country: string;
    checkIn: string | Date;
    checkOut: string | Date;
    selectedBoatType: BoatTypeOption | null;
  }
  const handleSearchClick = () => {
    let tempcity =
      selectedLocation?.type === "base"
        ? selectedLocation?.name
        : selectedLocation?.city || selectedLocation?.name || "";
    let dataForLocal: LocalSearchData = {
      city: tempcity,
      country: selectedLocation?.countryName,
      checkIn: checkIn,
      checkOut: checkOut,
      selectedBoatType,
    };
    dispatch(setLocalSearchData(dataForLocal));
    const selectedCheckIn = checkIn;
    const selectedCheckOut = checkOut;

    const country = selectedLocation?.shortName || "";

    const id = selectedLocation?.id || "";
    const type = selectedLocation?.type || "";

    const selectedBoatTypeLabel =
      selectedBoatType?.label === "All Types"
        ? ""
        : selectedBoatType?.label || "";

    trackEvent('search_clicked', {
      country,
      boat_type: selectedBoatTypeLabel || '',
      location_type: type || '',
      city: tempcity || '',
    });

    setLoadingClickSearch(true);
    onSearch(
      selectedCheckIn,
      selectedCheckOut,
      country,
      selectedBoatTypeLabel || "",
      0,
      tempcity,
      id,
      type,
      1
    );
    const data = {
      selectedCheckIn,
      selectedCheckOut,
      selectedLocation: { ...selectedLocation, city: tempcity },
      selectedBoatTypeLabel,
      selectedBoatType,
    };

    dispatch(setAllData(data));
    setLoadingClickSearch(false);
  };
  const searchBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = searchBarRef.current;
    if (!el) return;
    const update = () => requestAnimationFrame(() => document.documentElement.style.setProperty('--search-bar-height', `${el.offsetHeight}px`));
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => { obs.disconnect(); document.documentElement.style.removeProperty('--search-bar-height'); };
  }, []);

  const isFirstRender = React.useRef(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const dateFromRaw = params.get("date_from");
    const dateToRaw = params.get("date_to");

    const countryParam = params.get("country") || "";
    const cityParam = params.get("city") || "";
    const yachtTypeParam = params.get("yacht_type") || "";
    const type = params.get("type") || "";
    
    // Get the correct ID parameter based on type
    let id = "";
    if (type === "base") {
      id = params.get("base_from_id") || "";
    } else if (type === "sailing_area") {
      id = params.get("sailing_area_id") || "";
    } else if (type === "island") {
      id = params.get("island_name") || "";
    } else {
      id = params.get("id") || "";
    }

    // Helper to normalize date to 00:00:00
    const normalizeDateToMidnight = (dateString: any) => {
      const date = new Date(dateString);
      date.setUTCHours(0, 0, 0, 0);
      return date.toISOString();
    };

    const checkInParam = dateFromRaw
      ? normalizeDateToMidnight(dateFromRaw)
      : "";
    const checkOutParam = dateToRaw ? normalizeDateToMidnight(dateToRaw) : "";
    setCheckIn(checkInParam);
    setCheckOut(checkOutParam);
    setSelectedBoatType(yachtTypeParam);
    const countryObj = countriesJson.find(
      (country) => country.code.toLowerCase() === countryParam.toLowerCase()
    );

    const countryName = countryObj?.name || "";
    // For island type, id holds the island_name — use it as the display name
    const name = type === "island" && id ? id.charAt(0).toUpperCase() + id.slice(1) : "";
    dispatch(
      setLocation({
        countryName,
        city: cityParam,
        shortName: countryParam,
        type,
        id,
        name,
      })
    );

    setSelectedLocation({
      countryName,
      city: cityParam,
      shortName: countryParam,
      type,
      id,
      name,
    });

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    handleSearchClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  return (
    <Box ref={searchBarRef} sx={{ position: "fixed", top: 'calc(var(--banner-height, 0px) + var(--nav-height, 68px))', left: 0, right: 0, zIndex: 1000, transition: 'top 0.3s ease' }}>
      {/* Header Bar with Logo and Contact Info */}
      {/* <HeaderBar /> */}

      {/* Search Bar Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#ffffff",
          padding: "1.5rem 0",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          maxWidth="1180px"
          width="100%"
          padding="0 2rem"
        >
          <Grid container spacing={2} alignItems="center">
            {/* "Where" Field with Autocomplete */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={containerStyles}>
                <Box padding="5px 12px">
                  <Typography sx={labelStyles}>WHERE</Typography>
                  <Autocomplete
                    freeSolo
                    options={displayedOptions}
                    value={selectedLocation}
                    isOptionEqualToValue={(option, value) =>
                      option.countryName === value.countryName ||
                      option.country === value.country
                    }
                    getOptionLabel={(option) => {
                      const part1 = option.name || option.city || "";
                      const part2 = option.countryName || option.country || "";
                      if (typeof option === "string") {
                        return option;
                      }

                      return part1 && part2
                        ? `${part1}, ${part2}`
                        : part1
                          ? part1
                          : part2;
                    }}
                    filterOptions={(options) => options}
                    ListboxProps={{ style: { maxHeight: 380 } }}
                    onInputChange={(event, value, reason) => {
                      if (reason === "input") {
                        setSearchQuery(value);
                        setSearchResults([]);
                        handleLocationSearch(value);
                      }
                    }}
                    onChange={(event, value) => {
                      setSelectedLocation(value); // Update the selected location
                    }}
                    // renderOption={(props, option) => (
                    //   <li {...props}>
                    //     <Box
                    //       sx={{
                    //         display: "flex",
                    //         alignItems: "center",
                    //         width: "100%",
                    //       }}
                    //     >
                    //       <Box sx={{ marginRight: "1rem" }}>
                    //         <img
                    //           src={option.imageUrl}
                    //           alt={option.name}
                    //           style={{
                    //             width: "50px",
                    //             height: "50px",
                    //             borderRadius: "50%",
                    //           }}
                    //         />
                    //       </Box>
                    //       <Box>
                    //         {option.id ? (
                    //           // Show the option name if id exists
                    //           <Typography>{option.name}</Typography>
                    //         ) : (
                    //           // Show city and country if id doesn't exist
                    //           <>
                    //             <Typography>
                    //               {option.city},{" "}
                    //               {option.countryName || option.country}
                    //             </Typography>
                    //             <Typography
                    //               variant="body2"
                    //               color="textSecondary"
                    //             >
                    //               {option.countryName || option.country}
                    //             </Typography>
                    //           </>
                    //         )}
                    //       </Box>
                    //       <Badge
                    //         badgeContent={option.yachtCount}
                    //         color="primary"
                    //         sx={{ marginLeft: "auto" }}
                    //         max={10000}
                    //       />
                    //     </Box>
                    //   </li>
                    // )}
                    renderOption={(props, option) => {
                      const TYPE_LABEL: Record<string, string> = {
                        sailing_area: 'region',
                        island: 'island',
                        base: 'marina',
                        city: 'city',
                        country: 'country',
                      };
                      const flagUrl = (option as any).shortName
                        ? `https://flagcdn.com/w40/${(option as any).shortName.toLowerCase()}.png`
                        : null;

                      const bold = (text: string) => {
                        if (!searchQuery || !text) return <>{text}</>;
                        const lo = text.toLowerCase();
                        const qi = lo.indexOf(searchQuery.toLowerCase());
                        if (qi === -1) return <>{text}</>;
                        return <>{text.slice(0, qi)}<strong>{text.slice(qi, qi + searchQuery.length)}</strong>{text.slice(qi + searchQuery.length)}</>;
                      };

                      if (option.isSubItem) {
                        return (
                          <li {...props} key={props.key} style={{ padding: '2px 8px' }}>
                            <Box sx={{ display: "flex", alignItems: "center", width: "100%", pl: 3 }}>
                              {flagUrl && (
                                <img src={flagUrl} alt="" style={{ width: 18, height: 12, objectFit: "cover", borderRadius: 2, marginRight: 6, flexShrink: 0, opacity: 0.7 }} />
                              )}
                              <Typography variant="caption" sx={{ color: "#666", flexGrow: 1, fontSize: "12px" }}>
                                ⚓ {bold(option.name || option.city || "")}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#bbb", fontSize: "10px", ml: 1 }}>
                                marina
                              </Typography>
                            </Box>
                          </li>
                        );
                      }

                      const label = option.name || option.city || "";
                      const country = option.countryName || "";
                      const typeLabel = (option as any).type ? TYPE_LABEL[(option as any).type] : undefined;
                      const key = optKey(option);
                      const expandable = hasChildren.has(key);
                      const expanded = expandedIds.has(key);

                      return (
                        <li {...props} key={props.key} style={{ padding: '4px 16px 4px 8px' }}>
                          <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
                            {flagUrl ? (
                              <img src={flagUrl} alt={country} style={{ width: 24, height: 16, objectFit: "cover", borderRadius: 2, flexShrink: 0 }} />
                            ) : (
                              <Box sx={{ width: 24, height: 16, bgcolor: "#e0e0e0", borderRadius: 1, flexShrink: 0 }} />
                            )}
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap sx={{ fontSize: "13px" }}>
                                {bold(label)}{country ? <>, {bold(country)}</> : null}
                              </Typography>
                            </Box>
                            {typeLabel && (
                              <Typography
                                variant="caption"
                                sx={{ color: "#999", fontSize: "10px", bgcolor: "#f5f5f5", px: 0.6, py: 0.1, borderRadius: "3px", whiteSpace: "nowrap", flexShrink: 0 }}
                              >
                                {typeLabel}
                              </Typography>
                            )}
                            {expandable && (
                              <IconButton
                                size="small"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setExpandedIds(prev => {
                                    const next = new Set(prev);
                                    if (next.has(key)) next.delete(key); else next.add(key);
                                    return next;
                                  });
                                }}
                                sx={{ p: 0.2, color: "#aaa", flexShrink: 0 }}
                              >
                                {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            )}
                            <Box sx={{ width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Badge
                                badgeContent={option.yachtCount}
                                color="primary"
                                max={10000}
                                sx={{ '& .MuiBadge-badge': { fontSize: '10px', minWidth: '17px', height: '17px', position: 'static', transform: 'none' } }}
                              />
                            </Box>
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        variant="standard"
                        placeholder="Enter a city, region, etc."
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                          endAdornment: (
                            <>
                              {loadingLocation ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>

            {/* "Check-in" Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={containerStyles}>
                <Box padding="5px 12px" className="search-page">
                  <Grid position="relative" container>
                    <Grid item xs={6}>
                      <Typography sx={labelStyles}>CHECK-IN</Typography>
                    </Grid>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        position: "absolute",
                        top: "20%",
                        left: "50%",
                        height: "40px",
                      }}
                    />
                    <Grid item xs={6}>
                      <Typography sx={labelStyles} pl={2}>
                        CHECK-OUT
                      </Typography>
                    </Grid>
                  </Grid>

                  <SailRangePicker
                    startDate={checkIn ? new Date(checkIn) : null}
                    endDate={checkOut ? new Date(checkOut) : null}
                    onChange={(start, end) => {
                      const s = new Date(start); s.setHours(5, 0, 0, 0);
                      const e = new Date(end); e.setHours(5, 0, 0, 0);
                      setCheckIn(s.toISOString());
                      setCheckOut(e.toISOString());
                    }}
                    placeholder={["Check-in", "Check-out"]}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Search Button */}
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                sx={{
                  backgroundColor: "#f04e23",
                  borderRadius: "8px",
                  height: "100%",
                  fontSize: "1rem",
                  padding: "14px",
                }}
                startIcon={
                  loadingClickSearch ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )
                } // Show spinner during loading
                onClick={handleSearchClick}
                disabled={loadingClickSearch || !selectedLocation}
              >
                {loadingClickSearch ? "Searching..." : "Search"}{" "}
                {/* Change text during loading */}
              </Button>
            </Grid>
          </Grid>
        </Box>
        {error && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="error"
              sx={{ marginTop: "1rem" }}
            >
              {error}
            </Typography>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default HeaderWithSearch;
